import React, { useState } from 'react';
import { Edit2, Plus, UserX, UserCheck, Trash2, Eye, EyeOff, ShieldCheck, ShieldAlert, Key, Copy, Check } from 'lucide-react';
import CustomSelect from './CustomSelect';
import { getAuthorizedHeaders } from '../utils/authorizedHeaders';

const API_BASE = 'https://warngen-api-352514293915.europe-west1.run.app';


const UserManager = ({
    usuarios,
    nuevoUsuario,
    setNuevoUsuario,
    usuarioEditando,
    guardarUsuario,
    iniciarEdicionUsuario,
    cancelarEdicionUsuario,
    toggleUsuario,
    eliminarUsuario,
    usuario, // Usuario actual logueado
    darkMode
}) => {
    const [mostrarPasswordUsuario, setMostrarPasswordUsuario] = useState(false);

    // Estado para gestión de 2FA
    const [mfaSetupData, setMfaSetupData] = useState(null);
    const [mfaCodeInput, setMfaCodeInput] = useState('');
    const [mfaPasswordInput, setMfaPasswordInput] = useState('');
    const [mfaLoading, setMfaLoading] = useState(false);
    const [mfaMessage, setMfaMessage] = useState('');
    const [mfaError, setMfaError] = useState('');
    const [mfaCopied, setMfaCopied] = useState(false);
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [userMfaActive, setUserMfaActive] = useState(!!usuario?.mfa_enabled);

    const handleStartMfaSetup = async () => {
        setMfaLoading(true);
        setMfaError('');
        setMfaMessage('');
        try {
            const res = await fetch(`${API_BASE}/api/auth/mfa/setup`, {
                method: 'POST',
                headers: getAuthorizedHeaders()
            });
            const data = await res.json();
            if (data.success) {
                setMfaSetupData(data);
            } else {
                setMfaError(data.error || 'Error al iniciar configuración 2FA');
            }
        } catch (e) {
            setMfaError('Error de conexión con el servidor');
        }
        setMfaLoading(false);
    };

    const handleEnableMfa = async (e) => {
        e.preventDefault();
        setMfaLoading(true);
        setMfaError('');
        setMfaMessage('');
        try {
            const res = await fetch(`${API_BASE}/api/auth/mfa/enable`, {
                method: 'POST',
                headers: getAuthorizedHeaders(),
                body: JSON.stringify({ code: mfaCodeInput })
            });
            const data = await res.json();
            if (data.success) {
                setMfaMessage(data.message);
                setUserMfaActive(true);
                setMfaSetupData(null);
                setMfaCodeInput('');
                // Actualizar user en localstorage
                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                storedUser.mfa_enabled = true;
                localStorage.setItem('user', JSON.stringify(storedUser));
            } else {
                setMfaError(data.error || 'Código incorrecto');
            }
        } catch (e) {
            setMfaError('Error verificando el código');
        }
        setMfaLoading(false);
    };

    const handleDisableMfa = async (e) => {
        e.preventDefault();
        setMfaLoading(true);
        setMfaError('');
        setMfaMessage('');
        try {
            const res = await fetch(`${API_BASE}/api/auth/mfa/disable`, {
                method: 'POST',
                headers: getAuthorizedHeaders(),
                body: JSON.stringify({ password: mfaPasswordInput })
            });
            const data = await res.json();
            if (data.success) {
                setMfaMessage(data.message);
                setUserMfaActive(false);
                setShowDisableModal(false);
                setMfaPasswordInput('');
                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                storedUser.mfa_enabled = false;
                localStorage.setItem('user', JSON.stringify(storedUser));
            } else {
                setMfaError(data.error || 'Contraseña incorrecta');
            }
        } catch (e) {
            setMfaError('Error desactivando 2FA');
        }
        setMfaLoading(false);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setMfaCopied(true);
        setTimeout(() => setMfaCopied(false), 2500);
    };

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Gestión de Usuarios y Seguridad</h2>

            {/* SECCIÓN 1: SEGURIDAD DEL PERFIL / 2FA (ENS mp.acc.4) */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border-l-4 border-primary-500">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2">
                            {userMfaActive ? (
                                <ShieldCheck size={24} className="text-green-500" />
                            ) : (
                                <ShieldAlert size={24} className="text-amber-500" />
                            )}
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                                Autenticación en Dos Pasos (2FA / MFA) — Requisito ENS
                            </h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Protege tu cuenta con códigos temporales de un solo uso (TOTP RFC 6238) compatibles con Google Authenticator, Microsoft Authenticator o Authy.
                        </p>
                        <div className="mt-2">
                            {userMfaActive ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                                    2FA Activado y Conforme a ENS
                                </span>
                            ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                    2FA Desactivado (Recomendado para roles administrativos)
                                </span>
                            )}
                        </div>
                    </div>

                    <div>
                        {!userMfaActive && !mfaSetupData && (
                            <button
                                onClick={handleStartMfaSetup}
                                disabled={mfaLoading}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition flex items-center gap-2"
                            >
                                <Key size={16} />
                                Configurar 2FA
                            </button>
                        )}
                        {userMfaActive && (
                            <button
                                onClick={() => { setShowDisableModal(true); setMfaError(''); setMfaMessage(''); }}
                                className="border border-red-300 dark:border-red-700 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 px-4 py-2 rounded-lg font-medium text-sm transition"
                            >
                                Desactivar 2FA
                            </button>
                        )}
                    </div>
                </div>

                {mfaMessage && (
                    <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 rounded-lg text-sm">
                        {mfaMessage}
                    </div>
                )}
                {mfaError && (
                    <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg text-sm">
                        {mfaError}
                    </div>
                )}

                {/* MODAL / PANEL DE CONFIGURACIÓN 2FA */}
                {mfaSetupData && (
                    <div className="mt-6 p-5 bg-gray-50 dark:bg-gray-700/40 border dark:border-gray-600 rounded-lg space-y-4">
                        <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Paso 1: Añade la cuenta a tu aplicación de autenticación</h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                            <div className="space-y-2">
                                <p className="text-xs text-gray-600 dark:text-gray-300">
                                    Introduce esta clave secreta manualmente en tu app (Google Authenticator / Authy):
                                </p>
                                <div className="flex items-center gap-2 bg-white dark:bg-gray-800 p-2.5 rounded border dark:border-gray-600 font-mono text-sm font-bold text-primary-700 dark:text-primary-300">
                                    <span className="flex-1 select-all">{mfaSetupData.secret}</span>
                                    <button
                                        type="button"
                                        onClick={() => copyToClipboard(mfaSetupData.secret)}
                                        className="text-gray-500 hover:text-gray-700 p-1"
                                        title="Copiar clave"
                                    >
                                        {mfaCopied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                                    </button>
                                </div>
                                <p className="text-[11px] text-gray-500">
                                    Tipo: Basado en tiempo (TOTP) | Dígitos: 6 | Intervalo: 30s
                                </p>
                            </div>

                            <form onSubmit={handleEnableMfa} className="space-y-2 bg-white dark:bg-gray-800 p-4 rounded-lg border dark:border-gray-600">
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                                    Paso 2: Introduce el código de 6 dígitos que aparece en tu app
                                </label>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <input
                                        type="text"
                                        maxLength={6}
                                        pattern="[0-9]{6}"
                                        placeholder="000000"
                                        value={mfaCodeInput}
                                        onChange={(e) => setMfaCodeInput(e.target.value.replace(/\D/g, ''))}
                                        className="w-full sm:w-36 px-3 py-2 text-center text-lg font-mono tracking-widest border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                        required
                                    />
                                    <button
                                        type="submit"
                                        disabled={mfaCodeInput.length !== 6 || mfaLoading}
                                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-medium transition disabled:opacity-50"
                                    >
                                        {mfaLoading ? 'Verificando...' : 'Activar 2FA'}
                                    </button>
                                </div>
                            </form>
                        </div>

                        <div className="text-right">
                            <button
                                type="button"
                                onClick={() => setMfaSetupData(null)}
                                className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                            >
                                Cancelar configuración
                            </button>
                        </div>
                    </div>
                )}

                {/* MODAL DESACTIVAR 2FA */}
                {showDisableModal && (
                    <form onSubmit={handleDisableMfa} className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg space-y-3">
                        <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                            ¿Seguro que deseas desactivar la autenticación en dos pasos?
                        </p>
                        <p className="text-xs text-red-600 dark:text-red-400">
                            Introduce tu contraseña actual para confirmar:
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2 max-w-md">
                            <input
                                type="password"
                                placeholder="Tu contraseña actual"
                                value={mfaPasswordInput}
                                onChange={(e) => setMfaPasswordInput(e.target.value)}
                                className="flex-1 px-3 py-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                                required
                            />
                            <button
                                type="submit"
                                disabled={mfaLoading}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium transition"
                            >
                                Confirmar Desactivación
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowDisableModal(false)}
                                className="text-gray-500 hover:text-gray-700 px-3 py-2 text-sm"
                            >
                                Cancelar
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* SECCIÓN 2: FORMULARIO DE CREACIÓN/EDICIÓN (SOLO ADMINS) */}
            { (usuario?.rol === 'admin' || usuario?.rol === 'superadmin' || usuarioEditando) && (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                    {usuarioEditando ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
                </h3>

                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nombre Completo *</label>
                            <input
                                type="text"
                                value={nuevoUsuario.nombre}
                                onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, nombre: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-gray-100"
                                placeholder="Juan Pérez"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email *</label>
                            <input
                                type="email"
                                value={nuevoUsuario.email}
                                onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, email: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-gray-100"
                                placeholder="usuario@ejemplo.com"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Contraseña {usuarioEditando ? '(dejar vacío para no cambiar)' : '*'}
                            </label>
                            <div className="relative">
                                <input
                                    type={mostrarPasswordUsuario ? "text" : "password"}
                                    value={nuevoUsuario.password}
                                    onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, password: e.target.value })}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-gray-100"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setMostrarPasswordUsuario(!mostrarPasswordUsuario)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                                >
                                    {mostrarPasswordUsuario ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rol *</label>
                            <CustomSelect
                                options={[
                                    { value: 'operador', label: 'Operador (Gestión básica de alertas y contactos)' },
                                    { value: 'admin', label: 'Administrador (Control total del sistema)' },
                                    { value: 'superadmin', label: 'Superadministrador (Acceso raíz y seguridad)' }
                                ]}
                                value={nuevoUsuario.rol}
                                onChange={(val) => setNuevoUsuario({ ...nuevoUsuario, rol: val })}
                                placeholder="Seleccionar rol"
                                darkMode={darkMode}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        {usuarioEditando && (
                            <button
                                onClick={cancelarEdicionUsuario}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                            >
                                Cancelar
                            </button>
                        )}
                        <button
                            onClick={guardarUsuario}
                            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition font-semibold flex items-center gap-2"
                        >
                            {usuarioEditando ? <Edit2 size={18} /> : <Plus size={18} />}
                            {usuarioEditando ? 'Guardar Cambios' : 'Crear Usuario'}
                        </button>
                    </div>
                </div>
            </div>
            )}

            {/* SECCIÓN 3: LISTADO DE USUARIOS (SOLO ADMINS) */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Usuarios Registrados</h3>
                </div>

                {(() => {
                    if (!usuarios || usuarios.length === 0) {
                        return (
                            <div className="p-8 text-center text-gray-500">
                                No hay usuarios registrados
                            </div>
                        );
                    }

                    return (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
                                    <tr>
                                        <th className="py-3 px-4">Usuario</th>
                                        <th className="py-3 px-4">Email</th>
                                        <th className="py-3 px-4">Rol</th>
                                        <th className="py-3 px-4">2FA (ENS)</th>
                                        <th className="py-3 px-4">Estado</th>
                                        <th className="py-3 px-4">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {usuarios.map((usr) => (
                                        <tr key={usr.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                            <td className="py-3 px-4 font-medium text-gray-900 dark:text-gray-100">{usr.nombre}</td>
                                            <td className="py-3 px-4 text-gray-600 dark:text-gray-300">{usr.email}</td>
                                            <td className="py-3 px-4">
                                                <span className="px-2 py-1 bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300 text-xs rounded capitalize font-medium">
                                                    {usr.rol}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4">
                                                {usr.mfa_enabled ? (
                                                    <span className="px-2 py-0.5 bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 text-xs rounded font-medium">
                                                        Activado
                                                    </span>
                                                ) : (
                                                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 text-xs rounded">
                                                        No activo
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-3 px-4">
                                                {usr.activo ? (
                                                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-medium">Activo</span>
                                                ) : (
                                                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded font-medium">Inactivo</span>
                                                )}
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex gap-2 items-center">
                                                    <button
                                                        onClick={() => iniciarEdicionUsuario(usr)}
                                                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                                                    >
                                                        <Edit2 size={16} />
                                                        Editar
                                                    </button>
                                                    {usr.id !== usuario?.id ? (
                                                        <>
                                                            <button
                                                                onClick={() => toggleUsuario(usr.id)}
                                                                className={`text-sm flex items-center gap-1 ${usr.activo
                                                                    ? 'text-orange-500 hover:text-orange-700'
                                                                    : 'text-green-500 hover:text-green-700'
                                                                    }`}
                                                            >
                                                                {usr.activo ? (
                                                                    <>
                                                                        <UserX size={16} />
                                                                        Desactivar
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <UserCheck size={16} />
                                                                        Activar
                                                                    </>
                                                                )}
                                                            </button>
                                                            <button
                                                                onClick={() => eliminarUsuario(usr.id)}
                                                                className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                                                            >
                                                                <Trash2 size={16} />
                                                                Eliminar
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <span className="text-sm text-gray-500 italic ml-2">Tu cuenta</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

export default UserManager;